import { ActivityService, SendParticipantActivityLinkResponseStatus } from '@app/_services';
import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  ActivityActions,
  ToastActions,
  Participant,
  ParticipantStateModel,
  ParticipantActions,
  ActivityState,
  DialogActions,
  Notification,
  ParticipantProduct,
  EmailType,
  Metadata,
  Activity,
  Payment,
  PaymentRecipient,
} from '@app/_store';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { of, Observable } from 'rxjs';

import { patch, removeItem, updateItem, compose, append, insertItem } from '@ngxs/store/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { UiServices } from '@app/_helpers';

const defaults = {
  participants: undefined,
  notifications: undefined,
};

@State<ParticipantStateModel>({
  name: 'participants',
  defaults,
})
@Injectable()
export class ParticipantState {
  constructor(
    private activityService: ActivityService,
    private store: Store,
    private uiServices: UiServices
  ) {}

  @Selector()
  static participants(state: ParticipantStateModel): Participant[] {
    return state?.participants;
  }

  @Selector()
  static notifications(state: ParticipantStateModel): Notification[] {
    return state?.notifications;
  }

  static participant(id: string) {
    return createSelector([ParticipantState.participants], (state: Participant[]) => {
      return state.find(s => s.id == id);
    });
  }

  @Action(ParticipantActions.Reset)
  reset({ setState }: StateContext<ParticipantStateModel>): Observable<void> {
    setState(defaults);
    return of(undefined);
  }

  private mapActivityLinkEmailDistribution(notification: Notification) {
    if (!notification) return undefined;

    Notification.fixDates(notification);

    return notification;
  }

  @Action(ParticipantActions.Load)
  view(ctx: StateContext<ParticipantStateModel>): Observable<void> {
    return this.activityService.getParticipants().pipe(
      tap(participantList => {
        const activity = this.store.selectSnapshot(ActivityState.currentActivity);

        ctx.patchState({
          participants: participantList.participants.map(p => {
            p.metadata = this.mapMetadata(p.metadata, activity);

            p.payments = p.payments.map(payment => {
              payment.timestamp = new Date(payment.timestamp);
              return payment;
            });

            if (p.created) p.created = new Date(p.created);

            p.activityLinkQRCode = '';
            p.products = p.products.map(product => {
              product.timestamp = new Date(product.timestamp);
              if (product.deliveryState && product.deliveryState.delivered) {
                product.deliveryState.delivered = new Date(product.deliveryState.delivered);
              }

              return product;
            });

            return p;
          }),
        });
      }),
      tap(() =>
        setTimeout(() => {
          ctx.dispatch(new ActivityActions.UpdateCurrentActivityCountsInList());
        }, 500)
      ),
      concatMap(() => ctx.dispatch(new ActivityActions.CalculateSummary()))
    );
  }

  private mapMetadata(participantMetadata: Record<string, string | string[]>[] | Metadata[], activity: Activity): Metadata[] {
    let metadata = [] as Metadata[];
    if (activity.registration && participantMetadata) {
      metadata = Object.keys(participantMetadata).map(key => {
        if (key == 'packageName') {
          return {
            text: `Paket: ${participantMetadata[key]}`,
            key: key,
            important: true,
          } as Metadata;
        } else {
          const formControl = activity.registration.formControls.find(f => f.id == key);
          return {
            text: `${formControl ? formControl.title + ':' : ''} ${participantMetadata[key]}`,
            key: key,
            important: formControl ? formControl.important : false,
          } as Metadata;
        }
      });

      return metadata.sort((a, b) => Number(b.important) - Number(a.important));
    } else if (participantMetadata && Object.keys(participantMetadata).length === 0) {
      return [];
    } else {
      return [];
    }
  }

  @Action(ParticipantActions.UpdateParticipant)
  updateParticipant(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.UpdateParticipant) {
    return this.activityService.patchParticipant(action.group, action.participantId, action.updates).pipe(
      tap(response => {
        const activity = this.store.selectSnapshot(ActivityState.currentActivity);
        ctx.setState(
          patch<ParticipantStateModel>({
            participants: updateItem<Participant>(
              p => p.id == action.participantId,
              patch<Participant>({
                ...response,
                metadata: this.mapMetadata(response.metadata, activity),
              })
            ),
          })
        );
        const allParticipantsInGroup = ctx
          .getState()
          .participants.filter(
            p =>
              p.group == action.group &&
              p.paymentRecipients &&
              p.paymentRecipients.filter(pr => pr.id == action.participantId).length > -1
          );
        allParticipantsInGroup.forEach(participant => {
          ctx.setState(
            patch<ParticipantStateModel>({
              participants: updateItem<Participant>(
                p => p.id == participant.id,
                patch<Participant>({
                  paymentRecipients: updateItem<PaymentRecipient>(
                    p => p.id == action.participantId,
                    patch<PaymentRecipient>({
                      name: response.name,
                      email: response.email,
                      mobile: response.mobile,
                    })
                  ),
                })
              ),
            })
          );
        });
      }),
      tap(() => {
        const participant = ctx.getState().participants.find(p => p.id == action.participantId);
        ctx.dispatch(new ToastActions.ShowSuccess(`Ändringar på ${participant.name} har sparats!`));
      }),
      catchError(_ => {
        ctx.dispatch(new ToastActions.ShowError('Det uppstod ett fel när ändringarna skulle sparas. Kontakta support.'));
        throw _;
      })
    );
  }

  @Action(ParticipantActions.DeleteParticipant)
  deleteParticipant(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.DeleteParticipant): Observable<void> {
    return this.activityService.deleteParticipant(action.participant.group, action.participant.id).pipe(
      tap(() => {
        ctx.setState(
          patch<ParticipantStateModel>({
            participants: removeItem<Participant>(p => p.id === action.participant.id),
          })
        );
      }),
      switchMap(() => {
        return ctx.dispatch(new ToastActions.ShowSuccess(`${action.participant.name} har tagits bort från aktiviteten.`));
      }),
      switchMap(() => {
        return ctx.dispatch(new ActivityActions.CalculateSummary());
      }),
      catchError((e: HttpErrorResponse) => {
        if (e?.error?.title == 'CannotBeDeleted') {
          return ctx.dispatch(
            new ToastActions.ShowError('Deltagaren kan inte tas bort då den har produkter valda hos Kakservice.')
          );
        }
        return ctx.dispatch(new ToastActions.ShowError('Det uppstod ett fel när deltagaren skulle tas bort. Försök gärna igen.'));
      })
    );
  }

  @Action(ParticipantActions.DeleteParticipantGroup)
  deleteParticipantGroup(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.DeleteParticipantGroup
  ): Observable<void> {
    return this.activityService.deleteParticipantGroup(action.groupId).pipe(
      map(response => {
        const filtered = ctx.getState().participants.filter(p => p.group !== action.groupId);

        ctx.setState(
          patch<ParticipantStateModel>({
            participants: filtered,
          })
        );

        return response;
      }),
      switchMap(response => {
        return ctx.dispatch(new ToastActions.ShowSuccess(`${response.deleted} deltagare togs bort.`));
      }),
      switchMap(() => {
        return ctx.dispatch(new ActivityActions.CalculateSummary());
      }),
      catchError((e: HttpErrorResponse) => {
        if (e?.error?.title == 'CannotBeDeleted') {
          return ctx.dispatch(
            new ToastActions.ShowError('Gruppen kan inte tas bort då någon av deltagarna har produkter valda hos Kakservice.')
          );
        }
        return ctx.dispatch(new ToastActions.ShowError('Det uppstod ett fel när deltagarna skulle tas bort. Försök gärna igen.'));
      })
    );
  }

  @Action(ParticipantActions.AddParticipantsByTag)
  addParticipantsByTag(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.AddParticipantsByTag
  ): Observable<void> {
    return this.activityService.addParticipantsByTag(action.tag, action.includeEntireGroup).pipe(
      tap(response => {
        if (response.added === 0) {
          ctx.dispatch(
            new ToastActions.ShowSuccess(
              action.tag == 'all' ? `Alla deltagare är redan tillagda.` : `Alla deltagare från ${action.tag} är redan tillagda.`
            )
          );
        } else {
          ctx.dispatch(new ToastActions.ShowSuccess(`${response.added} deltagare lades till.`));
        }
      }),
      concatMap(() => {
        const activity = this.store.selectSnapshot(ActivityState.currentActivity);
        return ctx.dispatch(new ActivityActions.View(activity.id));
      })
    );
  }

  @Action(ParticipantActions.AddParticipantsByMember)
  addParticipantsByMember(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.AddParticipantsByMember
  ): Observable<void> {
    return this.activityService.addParticipantsByMember(action.participantId, action.includeEntireGroup).pipe(
      tap(response => {
        ctx.dispatch(new ToastActions.ShowSuccess(`${response.added} deltagare lades till.`));
      }),
      concatMap(() => {
        const activity = this.store.selectSnapshot(ActivityState.currentActivity);
        return ctx.dispatch(new ActivityActions.View(activity.id));
      })
    );
  }

  @Action(ParticipantActions.AddParticipant)
  addParticipant(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.AddParticipant): Observable<void> {
    let integrationData = undefined;
    const activity = this.store.selectSnapshot(ActivityState.currentActivity);
    if (activity.type === 'kakservice') {
      integrationData = {
        kakservice: {
          batchId: activity.integrationData['kakservice']['batchId'],
        },
      };
    }

    return this.activityService.addParticipant(action.name, action.email, action.mobile, integrationData).pipe(
      tap(response => {
        ctx.dispatch(new ToastActions.ShowSuccess(`${response.added} deltagare lades till.`));
      }),
      concatMap(() => {
        const activity = this.store.selectSnapshot(ActivityState.currentActivity);
        return ctx.dispatch(new ActivityActions.View(activity.id));
      })
    );
  }

  @Action(ParticipantActions.AddManualPayment)
  addManualPayment(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.AddManualPayment): Observable<void> {
    return this.activityService
      .addManualPayment(action.participant.group, action.participant.id, action.date, action.amount, action.text)
      .pipe(
        map(response => {
          ctx.setState(
            patch({
              participants: updateItem(
                x => x.id === action.participant.id,
                patch<Participant>({
                  payments: append([
                    {
                      ...response,
                      timestamp: new Date(response.timestamp),
                    },
                  ]),
                })
              ),
            })
          );
          return response;
        }),
        switchMap(() => {
          return ctx.dispatch(new ParticipantActions.CalculateTotals([action.participant.id]));
        }),
        switchMap(() => {
          return ctx.dispatch(new ToastActions.ShowSuccess(`Betalningen sparades på ${action.participant.name}.`));
        }),
        switchMap(() => {
          return ctx.dispatch(new ActivityActions.CalculateSummary());
        }),
        catchError(() => {
          return ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när betalningen skulle sparas'));
        })
      );
  }

  @Action(ParticipantActions.AddPaymentFromServerEvent)
  addPaymentFromServerEvent(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.AddPaymentFromServerEvent
  ): Observable<void> {
    action.payment.timestamp = new Date(action.payment.timestamp);
    ctx.setState(
      patch<ParticipantStateModel>({
        participants: updateItem<Participant>(
          p => p.id == action.participantId,
          patch<Participant>({
            payments: append([action.payment]),
          })
        ),
      })
    );

    return ctx
      .dispatch(new ParticipantActions.CalculateTotals([action.participantId]))
      .pipe(tap(() => ctx.dispatch(new ActivityActions.CalculateSummary())));
  }

  @Action(ParticipantActions.AddProductFromServerEvent)
  addProductFromServerEvent(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.AddProductFromServerEvent
  ): Observable<void> {
    const product = {
      ...action.product,
      timestamp: new Date(action.product.timestamp),
    };
    ctx.setState(
      patch<ParticipantStateModel>({
        participants: updateItem<Participant>(
          p => p.id == action.participantId,
          patch<Participant>({
            products: append([product]),
          })
        ),
      })
    );

    return ctx.dispatch(new ActivityActions.LockProduct(action.product.id)).pipe(
      concatMap(() => {
        return ctx.dispatch(new ParticipantActions.CalculateTotals([action.participantId]));
      }),
      tap(() => ctx.dispatch(new ActivityActions.CalculateSummary()))
    );
  }

  @Action(ParticipantActions.RemoveProductFromServerEvent)
  removeProductFromServerEvent(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.RemoveProductFromServerEvent
  ): Observable<void> {
    ctx.setState(
      patch<ParticipantStateModel>({
        participants: updateItem<Participant>(
          p => p.id == action.participantId,
          patch<Participant>({
            products: removeItem(pp => pp.id == action.productId),
          })
        ),
      })
    );

    return ctx
      .dispatch(new ParticipantActions.CalculateTotals([action.participantId]))
      .pipe(tap(() => ctx.dispatch(new ActivityActions.CalculateSummary())));
  }

  @Action(ParticipantActions.UpdateProductFromServerEvent)
  updateProductFromServerEvent(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.UpdateProductFromServerEvent
  ): Observable<void> {
    action.product.timestamp = new Date(action.product.timestamp);
    ctx.setState(
      patch<ParticipantStateModel>({
        participants: updateItem<Participant>(
          p => p.id == action.participantId,
          patch<Participant>({
            products: updateItem<ParticipantProduct>(pp => pp.id == action.product.id, patch<ParticipantProduct>(action.product)),
          })
        ),
      })
    );

    return ctx.dispatch(new ActivityActions.LockProduct(action.product.id)).pipe(
      concatMap(() => {
        return ctx.dispatch(new ParticipantActions.CalculateTotals([action.participantId]));
      }),
      tap(() => ctx.dispatch(new ActivityActions.CalculateSummary()))
    );
  }

  @Action(ParticipantActions.CalculateTotals)
  calculateTotals(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.CalculateTotals): Observable<void> {
    const participants = ctx.getState().participants.filter(p => action.participantIds.indexOf(p.id) !== -1);
    const reducer = (accumulator: number, curr: number) => accumulator + curr;
    const updates = [];
    participants.forEach(participant => {
      const products = participant.products;
      const payments = participant.payments;

      const productCount = products
        .map(r => r.quantity)
        .reduce((accumulator, current) => {
          return accumulator + current;
        }, 0);

      const productTotal = products.length > 0 ? products.map(p => p.amount * p.quantity).reduce(reducer) : 0;
      const paymentTotal = payments.length > 0 ? payments.map(p => p.amount).reduce(reducer) : 0;
      const profitTotal = products.filter(p => p.profit).reduce((sum, product) => sum + product.quantity * product.profit, 0);

      updates.push(
        updateItem(
          x => x.id === participant.id,
          patch<Participant>({
            productCount: productCount,
            total: productTotal,
            paymentTotal: paymentTotal,
            profitTotal: profitTotal,
          })
        )
      );
    });

    ctx.setState(
      patch({
        participants: compose(...updates),
      })
    );

    return of(null);
  }

  @Action(ParticipantActions.LoadNotifications)
  loadNotifications(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.LoadNotifications): Observable<void> {
    return this.activityService.getNotifications(action.emailType).pipe(
      map(notifications => {
        ctx.setState(
          patch({
            notifications: notifications.map(n => {
              Notification.fixDates(n);
              return n;
            }),
          })
        );
      })
    );
  }

  @Action(ParticipantActions.LoadGroupNotifications)
  loadGroupNotifications(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.LoadGroupNotifications
  ): Observable<void> {
    return this.activityService.getNotifications(undefined, action.participantGroup.id).pipe(
      map(notifications => {
        action.participantGroup.notifications = notifications;
      })
    );
  }

  @Action(ParticipantActions.UpdateOrAddNotification)
  updateOrAddNotification(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.UpdateOrAddNotification
  ): Observable<void> {
    ctx.setState(
      patch<ParticipantStateModel>({
        notifications: updateItem(i => i.id == action.notification.id, action.notification),
      })
    );

    return of();
  }

  @Action(ParticipantActions.AddProductToParticipant)
  addProductToParticipant(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.AddProductToParticipant
  ): Observable<void> {
    return this.activityService
      .addProductToParticipant(action.participant.id, {
        productReferenceId: action.product.id,
        quantity: action.quantity,
        comment: action.comment,
        options: action.options,
        delivered: action.delivered ? action.delivered.toISOString() : null,
      })
      .pipe(
        map(response => {
          ctx.setState(
            patch({
              participants: updateItem(
                x => x.id === action.participant.id,
                patch<Participant>({
                  products: append([
                    {
                      ...response,
                      timestamp: new Date(response.timestamp),
                    },
                  ]),
                })
              ),
            })
          );
          return response;
        }),
        switchMap(() => {
          return ctx.dispatch(new ActivityActions.LockProduct(action.product.id));
        }),
        switchMap(() => {
          return ctx.dispatch(new ParticipantActions.CalculateTotals([action.participant.id]));
        }),
        switchMap(() => {
          return ctx.dispatch(new ToastActions.ShowSuccess(`${action.product.title} lades till på ${action.participant.name}`));
        }),
        switchMap(() => {
          return ctx.dispatch(new ActivityActions.CalculateSummary());
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status == 406) {
            return ctx.dispatch(
              new DialogActions.Confirm(
                'Hoppsan!',
                err.error.detail,
                () => {
                  return of(null);
                },
                false
              )
            );
          }
          return ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när produkten/avgiften skulle läggas till'));
        })
      );
  }

  @Action(ParticipantActions.BatchAddProductToParticipants)
  batchAddProductToParticipant(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.BatchAddProductToParticipants
  ): Observable<void> {
    return this.activityService
      .batchAddProductToParticipant({
        participantIds: action.participants.map(p => p.id),
        productReferenceId: action.product.id,
        quantity: action.quantity,
      })
      .pipe(
        map(response => {
          const updates = response.map(result =>
            updateItem<Participant>(
              y => y.id == result.participantId,
              patch<Participant>({
                products: append([
                  {
                    ...result.product,
                    timestamp: new Date(result.product.timestamp),
                  },
                ]),
              })
            )
          );
          ctx.setState(
            patch({
              participants: compose(...updates),
            })
          );
          return response;
        }),
        switchMap(() => {
          return ctx.dispatch(new ActivityActions.LockProduct(action.product.id));
        }),
        switchMap(() => {
          return ctx.dispatch(new ParticipantActions.CalculateTotals(action.participants.map(p => p.id)));
        }),
        switchMap(() => {
          return ctx.dispatch(
            new ToastActions.ShowSuccess(`${action.product.title} lades till på ${action.participants.length} deltagare`)
          );
        }),
        switchMap(() => {
          return ctx.dispatch(new ActivityActions.CalculateSummary());
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status == 406) {
            return ctx.dispatch(
              new DialogActions.Confirm(
                'Hoppsan!',
                err.error.detail,
                () => {
                  return of(null);
                },
                false
              )
            );
          }
          return ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när produkten/avgiften skulle läggas till'));
        })
      );
  }

  @Action(ParticipantActions.AddCustomProductToParticipant)
  addCustomProductToParticipant(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.AddCustomProductToParticipant
  ): Observable<void> {
    return this.activityService
      .addProductToParticipant(action.participant.id, {
        title: action.title,
        amount: action.amount,
        quantity: action.quantity,
        comment: action.comment,
        options: action.options,
        delivered: action.delivered ? action.delivered.toISOString() : null,
        profit: action.profit,
      })
      .pipe(
        map(response => {
          ctx.setState(
            patch({
              participants: updateItem(
                x => x.id === action.participant.id,
                patch<Participant>({
                  products: append([
                    {
                      ...response,
                      timestamp: new Date(response.timestamp),
                    },
                  ]),
                })
              ),
            })
          );
          return response;
        }),
        switchMap(() => {
          return ctx.dispatch(new ParticipantActions.CalculateTotals([action.participant.id]));
        }),
        switchMap(() => {
          return ctx.dispatch(new ToastActions.ShowSuccess(`${action.title} lades till på ${action.participant.name}`));
        }),
        switchMap(() => {
          return ctx.dispatch(new ActivityActions.CalculateSummary());
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.status == 406) {
            return ctx.dispatch(
              new DialogActions.Confirm(
                'Hoppsan!',
                err.error.detail,
                () => {
                  return of(null);
                },
                false
              )
            );
          }
          return ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när produkten/avgiften skulle läggas till'));
        })
      );
  }

  @Action(ParticipantActions.UpdateParticipantProduct)
  updateParticipantProduct(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.UpdateParticipantProduct
  ): Observable<void> {
    return this.activityService
      .updateParticipantProduct(action.participant.id, action.product.id, {
        quantity: action.quantity,
        comment: action.comment,
        options: action.options,
        title: action.title,
        amount: action.amount,
        delivered: action.delivered ? action.delivered.toISOString() : null,
        profit: action.profit,
      })
      .pipe(
        map(response => {
          if (response.deliveryState?.delivered) {
            response.deliveryState.delivered = new Date(response.deliveryState.delivered);
          }
          response.timestamp = new Date(response.timestamp);
          ctx.setState(
            patch({
              participants: updateItem(
                x => x.id === action.participant.id,
                patch<Participant>({
                  products: updateItem(p => p.id == action.product.id, response),
                })
              ),
            })
          );
          return response;
        }),
        switchMap(() => {
          return ctx.dispatch(new ParticipantActions.CalculateTotals([action.participant.id]));
        }),
        switchMap(() => {
          return ctx.dispatch(
            new ToastActions.ShowSuccess(`${action.product.title} uppdaterades för ${action.participant.name}`)
          );
        }),
        switchMap(() => {
          return ctx.dispatch(new ActivityActions.CalculateSummary());
        }),
        catchError(() => {
          return ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när produkten/avgiften skulle uppdateras'));
        })
      );
  }

  @Action(ParticipantActions.MarkProductsAsDelivered)
  markProductsAsDelivered(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.MarkProductsAsDelivered) {
    return this.activityService.markProductsAsDelivered(action.participantId, action.items).pipe(
      map(response => {
        ctx.dispatch(new ParticipantActions.UpdateDeliveryState(action.items, response.delivered, response.deliveredBy));
      })
    );
  }

  @Action(ParticipantActions.UpdateDeliveryState)
  updateDeliveryState(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.UpdateDeliveryState) {
    const updates = [];
    action.items.map(item => {
      const update = updateItem<Participant>(
        p => p.id == item.participantId,
        patch<Participant>({
          products: updateItem<ParticipantProduct>(
            x => x.id == item.productId,
            patch<ParticipantProduct>({
              deliveryState: {
                delivered: new Date(action.delivered),
                deliveredBy: action.deliveredBy,
                recentlyDelivered: true,
              },
            })
          ),
        })
      );
      updates.push(update);
    });

    ctx.setState(
      patch({
        participants: compose(...updates),
      })
    );
  }

  @Action(ParticipantActions.DeleteParticipantProduct)
  deleteParticipantProduct(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.DeleteParticipantProduct
  ): Observable<void> {
    return this.activityService.deleteParticipantProduct(action.participant.id, action.product.id, action.forceDelete).pipe(
      map(response => {
        ctx.setState(
          patch({
            participants: updateItem(
              x => x.id === action.participant.id,
              patch<Participant>({
                products: removeItem(p => p.id === action.product.id),
              })
            ),
          })
        );
        return response;
      }),
      switchMap(() => {
        return ctx.dispatch(new ParticipantActions.CalculateTotals([action.participant.id]));
      }),
      switchMap(() => {
        return ctx.dispatch(new ToastActions.ShowSuccess(`${action.product.title} togs bort från ${action.participant.name}`));
      }),
      switchMap(() => {
        return ctx.dispatch(new ActivityActions.CalculateSummary());
      }),
      catchError(() => {
        return ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när produkten/avgiften skulle tas bort'));
      })
    );
  }

  @Action(ParticipantActions.DeletePayment)
  deletePayment(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.DeletePayment): Observable<void> {
    return this.activityService.deletePayment(action.participant.id, action.paymentId).pipe(
      map(response => {
        ctx.setState(
          patch({
            participants: updateItem(
              x => x.id === action.participant.id,
              patch<Participant>({
                payments: removeItem(p => p.id === action.paymentId),
              })
            ),
          })
        );
        return response;
      }),
      switchMap(() => {
        return ctx.dispatch(new ParticipantActions.CalculateTotals([action.participant.id]));
      }),
      switchMap(() => {
        return ctx.dispatch(new ActivityActions.CalculateSummary());
      }),
      switchMap(() => {
        return ctx.dispatch(new ToastActions.ShowSuccess(`Betalningen togs bort från ${action.participant.name}`));
      }),
      switchMap(() => {
        return ctx.dispatch(new ActivityActions.CalculateSummary());
      }),
      catchError(() => {
        return ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när betalningen skulle tas bort'));
      })
    );
  }

  @Action(ParticipantActions.MarkPaymentAsVerified)
  markPaymentAsVerified(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.MarkPaymentAsVerified
  ): Observable<void> {
    return this.activityService.markPaymentAsVerified(action.participant.id, action.paymentId).pipe(
      switchMap(response =>
        ctx.dispatch(
          new ParticipantActions.UpdatePaymentVerification(
            action.participant.id,
            action.paymentId,
            response.verified,
            response.verifiedBy
          )
        )
      ),
      switchMap(() =>
        ctx.dispatch(new ToastActions.ShowSuccess(`Betalningen från ${action.participant.name} är markerad som kontrollerad`))
      ),
      catchError(() =>
        ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när betalningen skulle markeras som kontrollerad'))
      )
    );
  }

  @Action(ParticipantActions.UpdatePaymentVerification)
  updatePaymentVerification(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.UpdatePaymentVerification
  ): void {
    ctx.setState(
      patch<ParticipantStateModel>({
        participants: updateItem<Participant>(
          p => p.id === action.participantId,
          patch<Participant>({
            payments: updateItem<Payment>(
              p => p.id === action.paymentId,
              patch<Payment>({ verified: new Date(action.verified), verifiedBy: action.verifiedBy })
            ),
          })
        ),
      })
    );
  }

  @Action(ParticipantActions.GetParticipantActivityLinkQRCode)
  getParticipantActivityLinkQRCode(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.GetParticipantActivityLinkQRCode
  ) {
    return this.activityService.getParticipantActivityLinkQRCode(action.participantId).pipe(
      tap(response => {
        ctx.setState(
          patch({
            participants: updateItem(
              x => x.id === action.participantId,
              patch<Participant>({
                activityLinkQRCode: response,
              })
            ),
          })
        );
      })
    );
  }

  @Action(ParticipantActions.SendParticipantActivityLink)
  sendParticipantActivityLink(
    ctx: StateContext<ParticipantStateModel>,
    action: ParticipantActions.SendParticipantActivityLink
  ): Observable<boolean> {
    return this.activityService.sendParticipantActivityLink(action.participant?.id, action.group?.id).pipe(
      map(response => {
        if (response.status == SendParticipantActivityLinkResponseStatus.success) {
          let text: string;
          if (action.participant) {
            if (action.participant.paymentRecipients && action.participant.paymentRecipients.length > 0) {
              const recipients = action.participant.paymentRecipients.map(pr => pr.name).join(' och ');
              text = `En aktivitetslänk har skickats via epost till ${recipients} som är mottagare för ${action.participant.name}`;
            } else {
              text = `En aktivitetslänk har skickats via epost till ${action.participant.name}`;
            }
          } else if (action.group) {
            text = `En aktivitetslänk har skickats via epost till alla mottagare i gruppen ${action.group.names.join(',')}`;
          } else {
            text = `En aktivitetslänk har skickats via epost till resterande deltagare`;
          }
          ctx.dispatch(new ToastActions.ShowSuccess(text));
          return true;
        } else if (response.status == SendParticipantActivityLinkResponseStatus.needToWait) {
          ctx.dispatch(
            new ToastActions.ShowWarning('Du måste vänta 5 minuter innan du kan skicka till återstående deltagare igen')
          );
          return false;
        } else {
          ctx.dispatch(new ToastActions.ShowError('Ett fel uppstod när aktivitetslänken skulle skickas'));
          return false;
        }
      }),
      tap((markLinkAsDelivered: boolean) => {
        if (!markLinkAsDelivered) return;

        const notifications = ctx.getState().notifications.filter(n => n.emailType === EmailType.ParticipantActivityLink);
        const participants = ctx.getState().participants;

        const inserts = [];
        if (!action.participant && !action.group) {
          participants.forEach(participant => {
            if (!notifications.find(n => n.group == participant.group)) {
              inserts.push(
                insertItem({
                  id: '',
                  recipient: participant.email,
                  emailType: EmailType.ParticipantActivityLink,
                  group: participant.group,
                  requested: new Date(),
                } as Notification)
              );
            }
          });
        } else if (action.group && !action.participant) {
          participants
            .filter(p => p.group == action.group.id)
            .forEach(participant => {
              if (!notifications.find(n => n.group == participant.group)) {
                inserts.push(
                  insertItem({
                    id: '',
                    recipient: participant.email,
                    emailType: EmailType.ParticipantActivityLink,
                    group: participant.group,
                    requested: new Date(),
                  } as Notification)
                );
              }
            });
        } else {
          participants
            .filter(p => p.group == action.participant.group)
            .forEach(participant => {
              if (!notifications.find(n => n.group == participant.group)) {
                inserts.push(
                  insertItem({
                    id: '',
                    recipient: participant.email,
                    emailType: EmailType.ParticipantActivityLink,
                    group: participant.group,
                    requested: new Date(),
                  } as Notification)
                );
              }
            });
        }

        ctx.setState(
          patch<ParticipantStateModel>({
            notifications: compose(...inserts),
          })
        );
      })
    );
  }

  @Action(ParticipantActions.DownloadReceipt)
  downloadReceipt(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.DownloadReceipt) {
    ctx.dispatch(new ToastActions.ShowSuccess('Skapar betalningsbekräftelse...'));

    const fileName = action.participantId.name + '.pdf';

    return this.activityService
      .downloadReceipt(action.participantId.group, action.participantId.id)
      .pipe(tap(receipt => this.uiServices.downloadFile(receipt, fileName, 'application/pdf')));
  }

  @Action(ParticipantActions.UpdateIntegrationData)
  updateIntegrationData(ctx: StateContext<ParticipantStateModel>, action: ParticipantActions.UpdateIntegrationData) {
    ctx.setState(
      patch<ParticipantStateModel>({
        participants: updateItem(
          x => x.id === action.participantId,
          patch<Participant>({
            integrationData: action.integrationData,
          })
        ),
      })
    );
  }
}
